import React, { useCallback,useMemo, useState } from 'react';
import {
  AbsenceFilesPlaceholderIcon,
  AutocompleteDropdown,
  Box,
  ClearAll,
  FlexList,
  FlexListPlaceholder,
  ListItem,
  NoSearchItemsPlaceholder,
  PaginationBar,
  SearchInput,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { OrganizationTypes } from '@/common/constants';
import { ListItemBase, ListItemType, OptionKey } from '@/common/types';
import { TipSide, TipWrapper } from '@/components/wrappers/TipWrapper';
import { useCurrentOrganization } from '@/modules/core/hooks';
import useHasAccess from '@/modules/core/hooks/useHasAccess';
import useOrganizationQuery from '@/modules/core/queries/useOrganization.query';
import { EMPLOYEE_STATUS_OPTIONS,EmployeeStatus } from '@/modules/employee/employee.constants';
import { useFraudReviewPermissions } from '@/modules/employee/Employee/FraudReview/hooks';
import {
  EMPLOYEE_COLUMN_WIDTHS,
  FILTER_WIDTHS,
  FRAUD_COLUMN_WIDTH,
  STATUS_COLUMN_WIDTH,
} from '@/modules/employee/Employees/EmployeesList/EmployeesList.constants';
import { useEmployeesList } from '@/modules/employee/Employees/EmployeesList/hooks';
import useEmployeesListQuery from '@/modules/employee/hooks/useEmployeesList.query';
import { EmployerStatus } from '@/modules/employer/employer.constants';
import { useListPagination } from '@/modules/employer/hooks';
import globalTheme from '@/styles/theme';

const FIRST_PAGE = 0;

const isEmployeeFlagged = (employee: { isFraudReviewRequired?: boolean }) =>
  Boolean(employee.isFraudReviewRequired);

const fraudDividerRowStyle: React.CSSProperties = {
  minHeight: '1px',
  padding: 0,
  border: 0,
  borderRadius: 0,
  background: globalTheme.colors.border1,
  marginTop: globalTheme.spacings.spacing8,
  marginBottom: globalTheme.spacings.spacing8,
};

const FRAUD_DIVIDER_ROW: ListItem = {
  id: 'fraud-review-divider',
  fields: [{ key: 'fraud-review-divider', title: '', type: ListItemType.Node, node: null }],
  rowStyle: fraudDividerRowStyle,
};

const SEARCH_INPUT_ICON_GUTTER = '40px';

const StyledSearchInput = styled(SearchInput)`
  && {
    padding-top: ${({ theme }) => theme.spacings.spacing8};
    padding-bottom: ${({ theme }) => theme.spacings.spacing8};
    padding-left: ${({ theme }) => theme.spacings.spacing16};
    padding-right: ${SEARCH_INPUT_ICON_GUTTER} !important;
  }
`;

const StyledAutocompleteDropdown = styled(AutocompleteDropdown)`
  &.ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
    padding-right: ${({ theme }) => theme.spacings.spacing24};
  }
`;

const employerFilterRequirements = [
  { organizationType: OrganizationTypes.system },
  { organizationType: OrganizationTypes.partner },
  { organizationType: OrganizationTypes.distributor },

  { organizationType: OrganizationTypes.employer, isException: true },
  { organizationType: OrganizationTypes.subsidiary, isException: true },
  { organizationType: OrganizationTypes.subgroup, isException: true },
];
const subsidiaryFilterRequirements = [
  { organizationType: OrganizationTypes.system },
  { organizationType: OrganizationTypes.partner },
  { organizationType: OrganizationTypes.distributor },
  { organizationType: OrganizationTypes.company },

  { organizationType: OrganizationTypes.subsidiary, isException: true },
  { organizationType: OrganizationTypes.subgroup, isException: true },
];
const columnGap = { marginRight: globalTheme.spacings.spacing12 };
const headers: ListItemBase[] = [
  {
    key: 'lastName',
    title: 'Last name',
    flex: `0 0 ${EMPLOYEE_COLUMN_WIDTHS.lastName}`,
    style: columnGap,
  },
  {
    key: 'firstName',
    title: 'First name',
    flex: `0 0 ${EMPLOYEE_COLUMN_WIDTHS.firstName}`,
    style: columnGap,
  },
  {
    key: 'SSN',
    title: 'SSN (last 4)',
    flex: `0 0 ${EMPLOYEE_COLUMN_WIDTHS.ssn}`,
    style: columnGap,
  },
  {
    key: 'employerEmployeeId',
    title: 'ER Employee ID',
    flex: `0 0 ${EMPLOYEE_COLUMN_WIDTHS.employerEmployeeId}`,
    style: columnGap,
  },
  {
    key: 'partnerEmployeeId',
    title: 'PT Employee ID',
    flex: `0 0 ${EMPLOYEE_COLUMN_WIDTHS.partnerEmployeeId}`,
    style: columnGap,
  },
  {
    key: 'employer',
    title: 'Employer/Subsidiary',
    flex: 1,
    style: columnGap,
  },
  {
    key: 'status',
    title: 'Status',
    flex: `0 0 calc(${STATUS_COLUMN_WIDTH} + ${globalTheme.spacings.spacing12} + ${FRAUD_COLUMN_WIDTH})`,
    style: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
    },
  },
];
const EmployeesList = () => {
  const {
    observingOrganization,
    observingMode,
    observingEmployer,
  } = useCurrentOrganization();
  const isEmployerObserved = useMemo(
    () => observingEmployer?.id !== undefined, [observingEmployer],
  );
  const hasEmployerFilter = useHasAccess(employerFilterRequirements);
  const hasSubsidiaryFilter = useHasAccess(subsidiaryFilterRequirements);
  const {
    page,
    perPage,
    currentSearch,
    filterParam,
    setSearchString,
    setFilterParam,
    setPage,
    setPerPage,
  } = useListPagination();
  const hasFilterParam = useMemo(
    () => filterParam && filterParam !== EmployeeStatus.Null,
    [filterParam],
  );
  const [searchEmployerId, setSearchEmployerId] = useState<OptionKey>('');
  const [searchSubsidiaryId, setSearchSubsidiaryId] = useState<OptionKey>('');
  const [currentSearchString, setCurrentSearchString] = useState<OptionKey>('');

  const [employerSearchText, setEmployerSearchText] = useState('');
  const [subsidiarySearchText, setSubsidiarySearchText] = useState('');

  const [cleaningInitiator, runSearchTextCleaningForAutocomplete] = useState(false);
  const { canViewAlert: showFraudIndicator } = useFraudReviewPermissions();
  const params = useMemo(() => ({
    page: page - 1 < 0 ? 0 : page - 1,
    perPage,
    searchString: currentSearch || '',
    ...(hasFilterParam ? { status: filterParam } : {}),
    ...(observingMode ? { observingOrganizationId: observingOrganization.id } : {}),
    ...(observingOrganization?.type
      ? { observingOrganizationType: observingOrganization?.type } : {}),
    ...(searchEmployerId ? { searchEmployerId: searchEmployerId as string } : {}),
    ...(searchSubsidiaryId ? { searchSubsidiaryId: searchSubsidiaryId as string } : {}),
  }), [
    currentSearch,
    filterParam,
    hasFilterParam,
    observingMode,
    observingOrganization,
    page,
    perPage,
    searchEmployerId,
    searchSubsidiaryId,
  ]);
  const { data, total, isLoading } = useEmployeesListQuery(params);
  const formattedList = useEmployeesList(data, showFraudIndicator);
  const rows = useMemo(() => {
    if (!showFraudIndicator) {
      return formattedList;
    }
    const firstNormalIndex = data.findIndex((employee) => !isEmployeeFlagged(employee));
    if (firstNormalIndex <= 0) {
      return formattedList;
    }
    return [
      ...formattedList.slice(0, firstNormalIndex),
      FRAUD_DIVIDER_ROW,
      ...formattedList.slice(firstNormalIndex),
    ];
  }, [formattedList, data, showFraudIndicator]);
  const serviceRowsCount = rows.length - formattedList.length;

  const isSubsidiary = useMemo(
    () => observingOrganization?.type === OrganizationTypes.subsidiary,
    [observingOrganization],
  );

  const isSubsidiaryFilterDisabled = useMemo(
    () => !searchEmployerId && (isSubsidiary || !isEmployerObserved),
    [searchEmployerId, isSubsidiary, isEmployerObserved],
  );

  const hasClearAll = useMemo(
    () => Boolean(currentSearch || searchEmployerId
      || (searchSubsidiaryId && !isSubsidiary) || hasFilterParam),
    [currentSearch, searchEmployerId, searchSubsidiaryId, hasFilterParam, isSubsidiary],
  );
  const handleClearAll = useCallback(() => {
    setSearchString('');
    setSearchEmployerId('');
    setSearchSubsidiaryId('');
    setCurrentSearchString('');
    setFilterParam(EmployeeStatus.Null);
    runSearchTextCleaningForAutocomplete(!cleaningInitiator);
  }, [
    cleaningInitiator,
    setFilterParam,
    setSearchString,
  ]);
  const placeholder = hasClearAll
    ? <FlexListPlaceholder />
    : (
      <NoSearchItemsPlaceholder
        placeholderTitle="There are no employees yet"
        node={<AbsenceFilesPlaceholderIcon size="xxlarge" color="iconSecondary" />}
      />
    );
  const employerAdditionalFetchParams = useMemo(() => ({
    type: OrganizationTypes.company,
    // ...(observingOrganization?.id ? { parentId: observingOrganization.id } : {}),
  }), []);

  const subsidiaryAdditionalFetchParams = useMemo(() => ({
    type: OrganizationTypes.subsidiary,
    parentId: searchEmployerId || observingEmployer?.id,
    statuses: [EmployerStatus.Active, EmployerStatus.PendingTermination, EmployerStatus.TerminationInProgress].join(','),
  }), [searchEmployerId, observingEmployer]);

  const {
    data: employerOptions = [],
    isLoading: isEmployerLoading,
  } = useOrganizationQuery(employerSearchText, employerAdditionalFetchParams);

  const {
    data: subsidiaryOptions = [],
    isLoading: isSubsidiariesLoading,
  } = useOrganizationQuery(subsidiarySearchText, subsidiaryAdditionalFetchParams);

  const handleChangeSearch = useCallback((id: OptionKey) => {
    setSearchString(id as string);
    setCurrentSearchString(id);
  }, [setSearchString]);

  const handleChangeEmployer = useCallback(
    (id: OptionKey) => {
      setSearchEmployerId(id);
      setPage(FIRST_PAGE);
      setSearchSubsidiaryId('');
    }, [setPage, setSearchEmployerId, setSearchSubsidiaryId],
  );

  const handleChangeSubsidiary = useCallback((id: OptionKey) => {
    setSearchSubsidiaryId(id);
    setPage(FIRST_PAGE);
  }, [setPage, setSearchSubsidiaryId]);

  return (
    <FlexList
      testId="EmployeesList"
      headers={headers}
      rows={rows}
      total={total}
      pad="spacing24"
      moreCount={perPage + serviceRowsCount}
      loading={isLoading}
      placeholder={placeholder}
      footer={(
        <PaginationBar
          testId="EmployeesList_pagination"
          page={page}
          total={total}
          pageSize={perPage}
          onChangePage={setPage}
          onChangePageSize={setPerPage}
        />
      )}
    >
      <>
        <Box margin={{ top: '-50px', bottom: '20px' }}>
          <ClearAll onClick={handleClearAll} isVisible={hasClearAll} />
        </Box>
        <Box direction="row" justify="between" align="center" pad={{ bottom: 'spacing16' }}>
          <Text
            margin={{ top: 'spacing8', bottom: 'spacing12', right: 'spacing12' }}
            weight="bold"
            color="textBody"
            style={{ whiteSpace: 'nowrap' }}
          >Employees: {total || 0}
          </Text>
          <Box direction="row" justify="end">
            <Box width={{ min: FILTER_WIDTHS.search, max: FILTER_WIDTHS.search }}>
              <StyledSearchInput
                hasSearchingHistory
                testId="EmployeesList"
                disabled={formattedList.length === 0 && !currentSearch}
                placeholder="EE name, SSN, ID"
                contentWidth={320}
                value={currentSearchString as string}
                onChange={handleChangeSearch}
                resetValue={cleaningInitiator}
              />
            </Box>

            {hasEmployerFilter && !isEmployerObserved && (
              <Box
                margin={{ left: 'spacing12' }}
                width={{ min: FILTER_WIDTHS.employer, max: FILTER_WIDTHS.employer }}
              >
                <StyledAutocompleteDropdown
                  testId="EmployeesList"
                  disabled={formattedList.length === 0 && !searchEmployerId && !searchSubsidiaryId}
                  name="Employer"
                  placeholder="Employer"
                  cleaningInitiator={cleaningInitiator}
                  onSearch={setEmployerSearchText}
                  options={employerOptions}
                  isLoading={isEmployerLoading}
                  value={searchEmployerId}
                  onChange={handleChangeEmployer}
                  className="ellipsis"
                />
              </Box>
            )}

            {hasSubsidiaryFilter && (
              <TipWrapper
                description={(
                  <span style={{ display: 'inline-block', width: '240px', textAlign: 'center' }}>
                    Searching by Subsidiary available after selection of an Employer.
                  </span>
                )}
                side={TipSide.bottom}
                isActive={isSubsidiaryFilterDisabled}
              >
                <Box
                  margin={{ left: 'spacing12' }}
                  width={{ min: FILTER_WIDTHS.subsidiary, max: FILTER_WIDTHS.subsidiary }}
                >
                  <StyledAutocompleteDropdown
                    testId="EmployeesList-subsidiaryFilter"
                    disabled={isSubsidiaryFilterDisabled}
                    onSearch={setSubsidiarySearchText}
                    name="Subsidiary"
                    placeholder="Subsidiary"
                    options={subsidiaryOptions}
                    cleaningInitiator={cleaningInitiator}
                    isLoading={isSubsidiariesLoading}
                    onChange={handleChangeSubsidiary}
                    value={searchSubsidiaryId}
                    {...isSubsidiary ? { defaultValue: `${observingOrganization.id}` } : {}}
                    // isSearchMode
                    className="ellipsis"
                  />
                </Box>
              </TipWrapper>
            )}

            <Box
              margin={{ left: 'spacing12' }}
              width={{ min: FILTER_WIDTHS.status, max: FILTER_WIDTHS.status }}
            >
              <SelectDropdown
                id="status"
                testId="EmployeesList-statusFilter"
                disabled={formattedList.length === 0 && !hasFilterParam}
                singleMode
                ellipsisMode
                activeTitle
                name="Activity status"
                prefix="Status"
                options={EMPLOYEE_STATUS_OPTIONS}
                value={filterParam || EmployeeStatus.Null}
                onChange={setFilterParam}
              />
            </Box>

          </Box>
        </Box>
      </>
    </FlexList>
  );
};

export default EmployeesList;
